'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { forwardRef, type PropsWithChildren } from 'react';
import { Controller, type FieldError, type FieldValues, type RegisterOptions } from 'react-hook-form';
import ReactSelect, { components, type GroupBase, type MultiValueRemoveProps, type Props, type SelectInstance } from 'react-select';
import { type SelectOption } from '@shared/utils/types/select-option';
import { FieldErrorMessage } from '../field-error-message/field-error-message';
import CloseIcon from '../svgs/close-icon';
import SvgErrorIcon from '../svgs/error-icon';
import styles from './field-controller.module.scss';
// eslint-disable-next-line import/no-unassigned-import
import './select.scss';
export type SelectFieldProps<Option = SelectOption, IsMulti extends boolean = boolean, Group extends GroupBase<Option> = GroupBase<Option>> = BaseFieldProps & Props<Option, IsMulti, Group> & {
  error?: FieldError;
  hint?: string;
  isRequired?: boolean;
  maximumSelectedOptions?: number;
  noOptionsLabel?: string;
  withoutController?: boolean;
};
type BaseFieldProps = {
  dataTestId?: string;
  displayLabel?: boolean;
  hasOptionalLabel?: boolean;
  id?: string;
  label: string;
  name: string;
  optionalLabel?: string;

  // eslint-disable-next-line react/no-unused-prop-types
  rules?: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'setValueAs' | 'valueAsDate' | 'valueAsNumber'>;
};
const FieldWrapper = t0 => {
  const $ = _c(21);
  const {
    children,
    dataTestId,
    displayLabel: t1,
    error,
    hasOptionalLabel: t2,
    hint,
    id,
    label,
    name,
    optionalLabel: t3
  } = t0;
  const displayLabel = t1 === undefined ? true : t1;
  const hasOptionalLabel = t2 === undefined ? true : t2;
  const optionalLabel = t3 === undefined ? "optional" : t3;
  const fieldId = name ?? id;
  const errorMessageId = `${fieldId}-error`;
  const t4 = dataTestId ?? fieldId;
  let t5;
  if ($[0] !== displayLabel || $[1] !== fieldId || $[2] !== hasOptionalLabel || $[3] !== label || $[4] !== optionalLabel) {
    t5 = displayLabel && <label className={classNames(styles.field__label, styles["field__label--select"])} htmlFor={fieldId}>{label} {hasOptionalLabel && `(${optionalLabel})`}</label>;
    $[0] = displayLabel;
    $[1] = fieldId;
    $[2] = hasOptionalLabel;
    $[3] = label;
    $[4] = optionalLabel;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== error || $[7] !== hint) {
    t6 = !error && hint && <p className={styles.field__hint}>{hint}</p>;
    $[6] = error;
    $[7] = hint;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== error?.message) {
    t7 = error?.message && <SvgErrorIcon className={styles["field__error-icon"]} />;
    $[9] = error?.message;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== error || $[12] !== errorMessageId) {
    t8 = error?.message && <FieldErrorMessage id={errorMessageId} message={error.message} />;
    $[11] = error;
    $[12] = errorMessageId;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== children || $[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
    t9 = <div className={styles.field} data-testid={t4}>{children}{t5}{t6}{t7}{t8}</div>;
    $[14] = children;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};
const MultiValueRemove = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <CloseIcon className="react-select__remove-selected-option-icon" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <components.MultiValueRemove {...props}>{t0}</components.MultiValueRemove>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const SelectFieldComponent = forwardRef<SelectInstance<SelectOption, boolean, GroupBase<SelectOption>>, SelectFieldProps>((t0, ref) => {
  const $ = _c(59);
  let error;
  let hint;
  let id;
  let isDisabled;
  let isMulti;
  let label;
  let maximumSelectedOptions;
  let name;
  let noOptionsLabel;
  let onChange;
  let optionalLabel;
  let options;
  let restSelectFieldProps;
  let t1;
  let t2;
  let value;
  if ($[0] !== t0) {
    ({
      displayLabel: t1,
      error,
      hint,
      id,
      isDisabled,
      isMulti,
      isRequired: t2,
      label,
      maximumSelectedOptions,
      name,
      noOptionsLabel,
      onChange,
      optionalLabel,
      options,
      value,
      ...restSelectFieldProps
    } = t0);
    $[0] = t0;
    $[1] = error;
    $[2] = hint;
    $[3] = id;
    $[4] = isDisabled;
    $[5] = isMulti;
    $[6] = label;
    $[7] = maximumSelectedOptions;
    $[8] = name;
    $[9] = noOptionsLabel;
    $[10] = onChange;
    $[11] = optionalLabel;
    $[12] = options;
    $[13] = restSelectFieldProps;
    $[14] = t1;
    $[15] = t2;
    $[16] = value;
  } else {
    error = $[1];
    hint = $[2];
    id = $[3];
    isDisabled = $[4];
    isMulti = $[5];
    label = $[6];
    maximumSelectedOptions = $[7];
    name = $[8];
    noOptionsLabel = $[9];
    onChange = $[10];
    optionalLabel = $[11];
    options = $[12];
    restSelectFieldProps = $[13];
    t1 = $[14];
    t2 = $[15];
    value = $[16];
  }
  const displayLabel = t1 === undefined ? true : t1;
  const isRequired = t2 === undefined ? false : t2;
  const inputId = name ?? id;
  const errorMessageId = `${inputId}-error`;
  const hasError = Boolean(error);
  const hasValue = (Array.isArray(value) ? value.length : value) || value && !isMulti;
  const optionsLength = Array.isArray(value) ? value.length : 0;
  const t3 = !isRequired && !isDisabled && restSelectFieldProps.hasOptionalLabel;
  const t4 = (Boolean(restSelectFieldProps.isClearable) || isMulti) && hasValue;
  const t5 = !displayLabel;
  let t6;
  if ($[17] !== hasError || $[18] !== hasValue || $[19] !== t4 || $[20] !== t5) {
    t6 = classNames("react-select-container", {
      "react-select-container--has-error": hasError,
      "react-select-container--has-value": hasValue,
      "react-select-container--is-clearable": t4,
      "react-select-container--no-label": t5
    });
    $[17] = hasError;
    $[18] = hasValue;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== hasError || $[23] !== hasValue) {
    t7 = {
      control: () => classNames("react-select", {
        "react-select--has-error": hasError,
        "react-select--has-value": hasValue
      })
    };
    $[22] = hasError;
    $[23] = hasValue;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== displayLabel) {
    t8 = {
      MultiValueRemove,
      ...(displayLabel ? {
        Placeholder: _temp
      } : {})
    };
    $[25] = displayLabel;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== isMulti || $[28] !== maximumSelectedOptions || $[29] !== optionsLength) {
    t9 = maximumSelectedOptions && isMulti ? () => optionsLength >= maximumSelectedOptions : _temp2;
    $[27] = isMulti;
    $[28] = maximumSelectedOptions;
    $[29] = optionsLength;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== noOptionsLabel) {
    t10 = () => noOptionsLabel;
    $[31] = noOptionsLabel;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== errorMessageId || $[34] !== hasError || $[35] !== inputId || $[36] !== isDisabled || $[37] !== isMulti || $[38] !== isRequired || $[39] !== onChange || $[40] !== options || $[41] !== ref || $[42] !== restSelectFieldProps || $[43] !== t10 || $[44] !== t6 || $[45] !== t7 || $[46] !== t8 || $[47] !== t9 || $[48] !== value) {
    t11 = <ReactSelect {...restSelectFieldProps} aria-describedby={errorMessageId} aria-invalid={hasError} aria-required={isRequired} className={t6} classNamePrefix="react-select" classNames={t7} components={t8} inputId={inputId} isDisabled={isDisabled} isMulti={isMulti} isOptionDisabled={t9} noOptionsMessage={t10} onChange={onChange} options={options} ref={ref} value={value} />;
    $[33] = errorMessageId;
    $[34] = hasError;
    $[35] = inputId;
    $[36] = isDisabled;
    $[37] = isMulti;
    $[38] = isRequired;
    $[39] = onChange;
    $[40] = options;
    $[41] = ref;
    $[42] = restSelectFieldProps;
    $[43] = t10;
    $[44] = t6;
    $[45] = t7;
    $[46] = t8;
    $[47] = t9;
    $[48] = value;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  let t12;
  if ($[50] !== displayLabel || $[51] !== error || $[52] !== hint || $[53] !== label || $[54] !== name || $[55] !== optionalLabel || $[56] !== t11 || $[57] !== t3) {
    t12 = <FieldWrapper displayLabel={displayLabel} error={error} hasOptionalLabel={t3} hint={hint} label={label} name={name} optionalLabel={optionalLabel}>{t11}</FieldWrapper>;
    $[50] = displayLabel;
    $[51] = error;
    $[52] = hint;
    $[53] = label;
    $[54] = name;
    $[55] = optionalLabel;
    $[56] = t11;
    $[57] = t3;
    $[58] = t12;
  } else {
    t12 = $[58];
  }
  return t12;
});
export const SelectField = t0 => {
  const $ = _c(16);
  let controlledValue;
  let name;
  let onChange;
  let restSelectFieldProps;
  let rules;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      isRequired: t1,
      name,
      onChange,
      rules,
      value: controlledValue,
      withoutController: t2,
      ...restSelectFieldProps
    } = t0);
    $[0] = t0;
    $[1] = controlledValue;
    $[2] = name;
    $[3] = onChange;
    $[4] = restSelectFieldProps;
    $[5] = rules;
    $[6] = t1;
    $[7] = t2;
  } else {
    controlledValue = $[1];
    name = $[2];
    onChange = $[3];
    restSelectFieldProps = $[4];
    rules = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  const isRequired = t1 === undefined ? false : t1;
  const withoutController = t2 === undefined ? false : t2;
  let t3;
  if ($[8] !== controlledValue || $[9] !== isRequired || $[10] !== name || $[11] !== onChange || $[12] !== restSelectFieldProps || $[13] !== rules || $[14] !== withoutController) {
    t3 = withoutController ? <SelectFieldComponent isRequired={isRequired} name={name} onChange={onChange} value={controlledValue} {...restSelectFieldProps} /> : <Controller name={name} render={t4 => {
      const {
        field: t5,
        fieldState: t6
      } = t4;
      const {
        onChange: fieldOnChange,
        value,
        ...restFieldProps
      } = t5;
      const {
        error
      } = t6;
      const isRequiredOrHasRequiredRule = isRequired || Boolean(rules?.required);
      return <SelectFieldComponent error={error} isRequired={isRequiredOrHasRequiredRule} onChange={(newValue, actionMeta) => {
        if (onChange) {
          onChange(newValue, actionMeta);
        }
        fieldOnChange(newValue);
      }} value={value} {...restSelectFieldProps} {...restFieldProps} />;
    }} rules={rules} />;
    $[8] = controlledValue;
    $[9] = isRequired;
    $[10] = name;
    $[11] = onChange;
    $[12] = restSelectFieldProps;
    $[13] = rules;
    $[14] = withoutController;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
};
function _temp() {
  return null;
}
function _temp2() {
  return false;
}